import { useTranslation } from 'react-i18next';
import { GUEST_LIKELIHOODS, GUEST_SIDES, RSVP_STATUSES, type GuestStats } from '@guabi/shared';

/**
 * The counts behind the headline numbers: who said what, who you think will
 * come, and whose side they are on.
 *
 * All three were already computed and none were shown, so the only way to learn
 * that eleven people are "probably not" was to filter the list and count the
 * rows. They sit under the headcounts because that is the question they answer —
 * the expected number is the anticipated number minus the people judged
 * unlikely, and this is the arithmetic behind it.
 *
 * **Zeros are printed.** A breakdown with holes in it cannot be checked against
 * the total, and "0 his parents" is itself an answer — it says the category
 * exists and nobody is in it, where a missing entry leaves you wondering whether
 * it was counted at all. A whole row is dropped only when every one of its
 * values is zero, which means that dimension has never been used.
 */
function Row({ label, items }: { label: string; items: { key: string; text: string }[] }) {
  if (items.length === 0) return null;
  return (
    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
      <span className="text-caption text-leaf-soft">{label}</span>
      <span className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        {items.map((item) => (
          <span key={item.key} className="tabular text-caption text-ink">
            {item.text}
          </span>
        ))}
      </span>
    </div>
  );
}

export function GuestBreakdown({ stats }: { stats: GuestStats }) {
  const { t } = useTranslation();

  const build = <K extends string>(
    keys: readonly K[],
    counts: Partial<Record<K, number>>,
    label: (key: K) => string,
  ) => {
    if (!keys.some((key) => (counts[key] ?? 0) > 0)) return [];
    return keys.map((key) => ({ key, text: `${String(counts[key] ?? 0)} ${label(key)}` }));
  };

  return (
    <div className="flex flex-col gap-2">
      <Row
        label={t('guests.rsvp')}
        items={build(RSVP_STATUSES, stats.byRsvp, (k) =>
          t(`guests.rsvpValue.${k}`).toLocaleLowerCase(),
        )}
      />
      <Row
        label={t('guests.likelihood')}
        items={build(GUEST_LIKELIHOODS, stats.byLikelihood, (k) =>
          t(`guests.likelihoodValue.${k}`).toLocaleLowerCase(),
        )}
      />
      <Row
        label={t('guests.side')}
        items={build(GUEST_SIDES, stats.bySide, (k) =>
          t(`guests.sideValue.${k}`).toLocaleLowerCase(),
        )}
      />
    </div>
  );
}
